/**
 * The three content-block renderers' logic — display-only, and defensive throughout. Nothing here
 * calls a store action.
 *
 * The block arrives from the RAW working document, so it is typed as `TextBlock` / `ImageBlock` /
 * `AudioBlock` without being guaranteed to be one: `asset` may be missing, `url` empty, `value`
 * absent. Every read is guarded and falls back to an empty string, which the components turn into
 * a labelled placeholder, never a broken image icon (§2-5).
 */
import { computed } from 'vue'

import type { ComputedRef } from 'vue'
import type { AudioBlock, ImageBlock, TextBlock } from './interfaces'

import { countGapTokens } from '../../lib/assessments/schema'

/** A url we can render: a non-empty, non-whitespace string. Anything else is "not set yet". */
const usableUrl = (url: unknown): string => (typeof url === 'string' ? url.trim() : '')

const usableText = (value: unknown): string => (typeof value === 'string' ? value : '')

// --- text ------------------------------------------------------------------

interface UsePreviewContentTextReturn {
  /** Rendered as TEXT, never as HTML — see the component; there is no `v-html` in this feature. */
  refValue: ComputedRef<string>
  refIsEmpty: ComputedRef<boolean>
  /** A `{{…}}` in a plain text block does nothing — gaps belong to `fill_in_blanks` — and the
   *  marker looks like it should work anywhere. Counts the ANSWER-CARRYING form too
   *  (`countGapTokens`), since an author who typed `{{five}}` into a prompt most needs telling. */
  refHasStrayGapMarker: ComputedRef<boolean>
}

export const usePreviewContentText = (
  block: () => TextBlock,
): UsePreviewContentTextReturn => {
  const refValue = computed<string>(() => usableText(block()?.value))
  const refIsEmpty = computed<boolean>(() => refValue.value.trim().length === 0)
  const refHasStrayGapMarker = computed<boolean>(() => countGapTokens(refValue.value) > 0)

  return { refValue, refIsEmpty, refHasStrayGapMarker }
}

// --- image -----------------------------------------------------------------

interface UsePreviewContentImageReturn {
  refUrl: ComputedRef<string>
  refHasUrl: ComputedRef<boolean>
  refAlt: ComputedRef<string>
  refHasAlt: ComputedRef<boolean>
}

export const usePreviewContentImage = (
  block: () => ImageBlock,
): UsePreviewContentImageReturn => {
  const refUrl = computed<string>(() => usableUrl(block()?.asset?.url))
  const refHasUrl = computed<boolean>(() => refUrl.value.length > 0)
  const refAlt = computed<string>(() => usableText(block()?.alt))
  const refHasAlt = computed<boolean>(() => refAlt.value.trim().length > 0)

  return { refUrl, refHasUrl, refAlt, refHasAlt }
}

// --- audio -----------------------------------------------------------------

interface UsePreviewContentAudioReturn {
  refUrl: ComputedRef<string>
  refHasUrl: ComputedRef<boolean>
}

export const usePreviewContentAudio = (
  block: () => AudioBlock,
): UsePreviewContentAudioReturn => {
  const refUrl = computed<string>(() => usableUrl(block()?.asset?.url))
  const refHasUrl = computed<boolean>(() => refUrl.value.length > 0)

  return { refUrl, refHasUrl }
}
